import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash, FaFileDownload, FaArrowLeft, FaUser, FaCalendarAlt } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';
import AuthContext from '../../context/AuthContext';

const PrescriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [prescription, setPrescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  console.log("prescription", prescription);

  useEffect(() => {
    fetchPrescription();
  }, [id]);

  const fetchPrescription = async () => {
    try {
      setLoading(true);
            const res = await api.get(`/prescriptions/${id}`);
      setPrescription(res.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching prescription data:', err);
      setError('Failed to load prescription data');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
            await api.delete(`/prescriptions/${id}`);
      toast.success('Prescription deleted successfully');
      navigate('/prescriptions');
    } catch (err) {
      console.error('Error deleting prescription:', err);
      toast.error(err.response?.data?.msg || 'Failed to delete prescription');
    }
    setShowDeleteModal(false);
  };

  const handleDownload = async () => {
    try {
            const response = await api.get(`/prescriptions/download/${id}`, {
        responseType: 'blob'
      });
      
      // Create a blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prescription-${id}.pdf`);
      
      // Append to html page
      document.body.appendChild(link);
      
      // Force download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      
      toast.success('Prescription downloaded successfully');
    } catch (err) {
      console.error('Error downloading prescription:', err);
      toast.error('Failed to download prescription');
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
        <Button as={Link} to="/prescriptions" variant="primary">
          <FaArrowLeft className="me-2" /> Back to Prescriptions
        </Button>
      </Container>
    );
  }

  if (!prescription) {
    return (
      <Container>
        <Alert variant="warning">Prescription not found</Alert>
        <Button as={Link} to="/prescriptions" variant="primary">
          <FaArrowLeft className="me-2" /> Back to Prescriptions
        </Button>
      </Container>
    );
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>Prescription Details</h1>
          <p className="text-muted">
            <FaCalendarAlt className="me-1" /> 
            {new Date(prescription.prescriptionDate).toLocaleDateString()}
            {prescription.expiryDate && (
              <span> (Expires: {new Date(prescription.expiryDate).toLocaleDateString()})</span>
            )}
          </p>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/prescriptions" variant="outline-secondary" className="me-2">
            <FaArrowLeft className="me-1" /> Back
          </Button>
          <Button as={Link} to={`/prescriptions/edit/${id}`} variant="outline-primary" className="me-2">
            <FaEdit className="me-1" /> Edit
          </Button>
          <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>
            <FaTrash className="me-1" /> Delete
          </Button>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Basic Information</Card.Header>
            <Card.Body>
              <p>
                <strong>Customer:</strong>{' '}
                <Link to={`/customers/${prescription.customer._id}`}>
                  <FaUser className="me-1" /> {prescription.customer.name}
                </Link>
              </p>
              <p><strong>Doctor:</strong> {prescription.doctor.name}</p>
              <p><strong>Type:</strong> {prescription.prescriptionType ? (prescription.prescriptionType.replace('_', ' ').charAt(0).toUpperCase() + prescription.prescriptionType.replace('_', ' ').slice(1)) : 'N/A'}</p>
              <p><strong>Date:</strong> {new Date(prescription.prescriptionDate).toLocaleDateString()}</p>
              <p>
                <strong>Expiry Date:</strong>{' '}
                {prescription.expiryDate ? new Date(prescription.expiryDate).toLocaleDateString() : 'Not specified'}
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Prescription Image</Card.Header>
            <Card.Body>
              {prescription.prescriptionImage ? (
                <div className="text-center">
                  <Button 
                    variant="primary" 
                    onClick={handleDownload}
                  >
                    <FaFileDownload className="me-1" /> Download Prescription
                  </Button>
                </div>
              ) : (
                <p className="text-muted">No prescription image uploaded</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>Prescription Details</Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Right Eye</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th>Sphere (SPH)</th>
                        <td>{prescription.prescriptionDetails.rightEye?.sphere || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Cylinder (CYL)</th>
                        <td>{prescription.prescriptionDetails.rightEye?.cylinder || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Axis</th>
                        <td>{prescription.prescriptionDetails.rightEye?.axis || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Add</th>
                        <td>{prescription.prescriptionDetails.rightEye?.add || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>PD</th>
                        <td>{prescription.prescriptionDetails.rightEye?.pd || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
                
                <Col md={6}>
                  <h5>Left Eye</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th>Sphere (SPH)</th>
                        <td>{prescription.prescriptionDetails.leftEye?.sphere || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Cylinder (CYL)</th>
                        <td>{prescription.prescriptionDetails.leftEye?.cylinder || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Axis</th>
                        <td>{prescription.prescriptionDetails.leftEye?.axis || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Add</th>
                        <td>{prescription.prescriptionDetails.leftEye?.add || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>PD</th>
                        <td>{prescription.prescriptionDetails.leftEye?.pd || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>
              
              {prescription.prescriptionDetails.notes && (
                <Row className="mt-4">
                  <Col md={12}>
                    <h5>Notes</h5>
                    <p>{prescription.prescriptionDetails.notes}</p>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>Actions</Card.Header>
            <Card.Body>
              <div className="d-flex gap-2">
                <Button 
                  as={Link} 
                  to={`/invoices/new/${prescription.customer._id}?prescription=${prescription._id}`} 
                  variant="success"
                >
                  Create Invoice from this Prescription
                </Button>
                
                {prescription.prescriptionImage && (
                  <Button 
                    variant="info" 
                    onClick={handleDownload}
                  >
                    <FaFileDownload className="me-1" /> Download Prescription
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this prescription?</p>
          <p className="text-danger">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PrescriptionDetail;
